import { useTransaction } from "hooks/useTransactions";
import * as i18n from "util/i18n";
import Button from "pages/_components/Button";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import { TransactionCard, TransactionHeader } from "pages/_components/TransactionCard";
import React, { useState } from "react";
import { bool, shape, string } from "prop-types";
import { Col, Row } from "react-bootstrap";
import Notification from "pages/_components/Notification";
import { useLocation } from "react-router-dom";
import { actions as debinActions } from "reducers/debin";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import * as Yup from "yup";
import Credential from "pages/_components/fields/credentials/Credential";

import CustomButton from "pages/_components/CustomButton/CustomButton";
import { withFormik, Form, Field } from "formik";
import { connect } from "react-redux";
import { compose } from "redux";
import DebinChargebackSummaryBody from "./SummaryBody";

const DebinChargebackTransactionPage = (props) => {
    const { state = {} } = useLocation();
    const { isFlowComplete = false } = state;
    const { isDesktop, isCancellingTransaction } = props;
    const [showToken, setShowToken] = useState(false);

    const formatTransactionData = (data, idTransaction) => {
        const formData = {
            sellerName: data?.sellerName,
            debitAccountMask: data?.debitAccountMask,
            quantity: data?.quantity,
            currency: data?.currency,
            issuedDate: data?.issuedDate,
            expirationDate: data?.expirationDate,
            debinId: data?.debinId,
            sellerCbu: data?.sellerCbu,
            sellerAlias: data?.sellerAlias,
            sellerBank: data?.sellerBank,
            selerBranch: data?.selerBranch,
            concept: data?.concept,
            quote: data?.quote,
            reference: data?.reference,
            idTransaction,
        };

        return { ...formData, idTransaction };
    };

    const {
        isLoading,
        transaction,
        dispatch,
        handleSignTransaction,
        showSignButton,
        push,
        id,
        status,
    } = useTransaction(
        "/debin/chargeBack",
        debinActions.setDebinToChargeBack,
        debinActions.cleanDebinTicket,
        formatTransactionData,
    );

    const FORM_ID = "debin.chargeBack";

    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        i18n.get(`${FORM_ID}.title`),
        id,
        "debin.chargeBack.transaction",
    );
    const [showMoreData, setShowMoreData] = useState(!isDesktop);

    const { isFinished, isBackoffice, isPending, isFailed, isCancelled } = status;

    const backLink = () => {
        let link;
        if (isDesktop) {
            if (isFlowComplete) {
                link = "/debin.chargeBack.transaction";
            } else {
                link = "";
            }
        } else {
            link = "/debin";
        }
        return link;
    };
    const backLinkTo = backLink();

    const handleDownload = async () => {
        await setShowMoreData(true);
        return handlePrint();
    };

    const goHomeButton = () => (
        <Col xs={12} md={4} mdOffset={2}>
            <CustomButton
                func={() => dispatch(push("/desktop"))}
                text="forms.transaction.ticket.actions.backToHome"
                textType="I18N"
                className="btn btn-outline"
            />
        </Col>
    );

    const signButton = () => (
        <Col xs={12} md={4}>
            <CustomButton
                func={() => setShowToken(false)}
                onClick={handleSignTransaction}
                text="forms.signTransaction.link"
                textType="I18N"
                className="btn btn-primary"
            />
        </Col>
    );

    const cancelButton = () =>
        !isCancellingTransaction ? (
            <>
                {goHomeButton()}
                <Col xs={12} md={4}>
                    <CustomButton
                        func={() => dispatch(formActions.changeCancelTransaction(true))}
                        text="forms.cancelTransaction.link"
                        textType="I18N"
                        className="btn btn-primary"
                    />
                </Col>
            </>
        ) : (
            <Form className="above-the-fold container--layout">
                <Row>
                    <Col xs={12} md={4} mdOffset={2}>
                        {isCancellingTransaction && (
                            <Field
                                idForm="form.credential"
                                name="password"
                                labelText="form.credential.password.label"
                                placeholderText="Ej: Ju@n_123"
                                component={Credential}
                                type="password"
                            />
                        )}
                    </Col>
                    <Col xs={12} md={4} className="align-items-end">
                        <div className="form-group hidden-label">
                            <div className="button-group">
                                <Button block type="submit" label="global.send" bsStyle="primary" loading={isLoading} />
                            </div>
                        </div>
                    </Col>
                </Row>
            </Form>
        );

    const downloadOrShareButton = () =>
        isDesktop ? (
            <Col xs={12} md={4}>
                <Button
                    className="btn btn-primary"
                    image="images/figma/icon/download.svg"
                    label="global.download"
                    onClick={handleDownload}
                />
            </Col>
        ) : (
            <Col xs={12} md={4}>
                <Button
                    className="btn btn-primary"
                    image="images/figma/icon/share.svg"
                    label="global.share"
                    onClick={handleShareMobile}
                />
            </Col>
        );

    return (
        <>
            {isFinished && <Notification scopeToShow="debin.chargeBack.transaction" />}
            {!isBackoffice && (
                <ResponsiveHeader
                    ticket
                    id={`${FORM_ID}.title`}
                    metaTitle={`${FORM_ID}.title`}
                    backLinkTo={backLinkTo}
                />
            )}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader title={`${FORM_ID}.title`} showLogo={false} {...props} />
                            <div className="ticket">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    <DebinChargebackSummaryBody
                                        ticket
                                        {...props}
                                        isFinished={isFinished}
                                        setShowMoreData={setShowMoreData}
                                        showMoreData={showMoreData}
                                        isDesktop={isDesktop}
                                    />
                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                </div>
                {!isBackoffice && (
                    <Row className="form-actions">
                        {isPending && (
                            <>
                                {!showSignButton && <>{cancelButton()}</>}

                                {showSignButton && (
                                    <>
                                        {goHomeButton()}
                                        {signButton()}
                                    </>
                                )}
                            </>
                        )}

                        {(isFinished || isFailed || isCancelled) && (
                            <>
                                {goHomeButton()} {downloadOrShareButton()}
                            </>
                        )}
                    </Row>
                )}
            </MainContainer>
        </>
    );
};

DebinChargebackTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
    transaction: shape({}).isRequired,
    isCancellingTransaction: bool.isRequired,
    idTransaction: string.isRequired,
};

const mapStateToProps = (state) => ({
    transaction: formSelectors.getTransaction(state),
    isCancellingTransaction: formSelectors.getIsCancellingTransaction(state),
    idTransaction: formSelectors.getTransaction(state).idTransaction,
});

export default compose(
    connect(mapStateToProps),
    withFormik({
        validateOnChange: false,
        validateOnBlur: false,
        mapPropsToValues: (props) => ({
            id: props.match.params.id,
            transaction: props.transaction,
            isCancellingTransaction: props.isCancellingTransaction,
            credential: "",
            password: "",
        }),
        validationSchema: (props) =>
            Yup.object().shape({
                credential: !props.isCancellingTransaction
                    ? Yup.string().required(i18n.get("deposits.request.required"))
                    : Yup.string().notRequired(),
                password: props.isCancellingTransaction
                    ? Yup.string().required(i18n.get("deposits.request.required"))
                    : Yup.string().notRequired(),
            }),
        handleSubmit: ({ password }, formikBag) => {
            const { dispatch, isCancellingTransaction } = formikBag.props;

            if (isCancellingTransaction) {
                dispatch(formActions.cancelTransaction({ credentials: { password, otp: "" }, formikBag }));
            }
        },
    }),
)(DebinChargebackTransactionPage);
